import { useState } from 'react';
import { inventarioService } from '../../services/api';
import Dialogo from '../../components/ui/Dialogo';
import { btnPrimario, btnSecundario } from '../../components/ui/estilos';
import { Campo, dosColumnas, entrada } from '../agenda/campos';
import { MOTIVOS, cant, num } from './insumos';

export type TipoMov = 'entrada' | 'salida' | 'merma';
const TITULO: Record<TipoMov, string> = { entrada: 'Registrar entrada', salida: 'Registrar salida', merma: 'Registrar merma' };
const HECHO: Record<TipoMov, string> = { entrada: 'Entrada', salida: 'Salida', merma: 'Merma' };

function Botones({ onCerrar, onOk, ok, ocupado }: { onCerrar: () => void; onOk: () => void; ok: string; ocupado: boolean }) {
  return (
    <div style={{ display: 'flex', gap: 10 }}>
      <button onClick={onCerrar} style={{ ...btnSecundario, flex: 1, height: 52 }}>Cancelar</button>
      <button onClick={onOk} disabled={ocupado} style={{ ...btnPrimario, flex: 2, height: 52, fontSize: 16, opacity: ocupado ? .6 : 1 }}>{ok}</button>
    </div>
  );
}

// Entrada, salida o merma: cantidad + motivo obligatorio (el stock no se escribe a mano).
export function DialogoMovimiento({ insumo, tipo, onCerrar, onHecho }: { insumo: any; tipo: TipoMov; onCerrar: () => void; onHecho: (msg: string) => void }) {
  const [cantidad, setCantidad] = useState('');
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const registrar = async () => {
    const c = num(cantidad);
    if (!(c > 0)) { setError('Escribe una cantidad mayor a 0.'); return; }
    if (!motivo.trim()) { setError('Explica el motivo.'); return; }
    setOcupado(true);
    try {
      if (tipo === 'merma') await inventarioService.crearMerma({ insumo_id: insumo.id, cantidad: c, motivo: motivo.trim() });
      else await inventarioService.registrarMovimiento(insumo.id, { tipo, cantidad: c, motivo: motivo.trim() });
      onHecho(`${HECHO[tipo]} de ${cant(c)} ${insumo.unidad} de ${insumo.nombre}.`);
    } catch (e: any) { setError(e.response?.data?.error || 'No se pudo registrar.'); setOcupado(false); }
  };

  return (
    <Dialogo titulo={TITULO[tipo]} onCerrar={onCerrar} ancho={440}>
      <div style={{ fontSize: 15, color: 'var(--muted)' }}>{insumo.nombre} · hay {cant(insumo.stock_actual)} {insumo.unidad}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {MOTIVOS[tipo].map(m => (
          <button key={m} type="button" onClick={() => setMotivo(m)} aria-pressed={motivo === m}
            style={{ ...btnSecundario, height: 40, fontSize: 14, borderColor: motivo === m ? 'var(--brand-texto)' : undefined, background: motivo === m ? 'var(--well)' : undefined }}>{m}</button>
        ))}
      </div>
      <input value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Motivo" aria-label="Motivo" maxLength={200} style={entrada} />
      <input autoFocus type="number" min="0" step="any" inputMode="decimal" value={cantidad} onChange={e => setCantidad(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && registrar()} placeholder={`Cantidad (${insumo.unidad})`} aria-label="Cantidad" style={entrada} />
      {error && <div role="alert" style={{ color: 'var(--danger)', fontWeight: 700 }}>{error}</div>}
      <Botones onCerrar={onCerrar} onOk={registrar} ok="Registrar" ocupado={ocupado} />
    </Dialogo>
  );
}

// Alta y edición. Al editar no se toca el stock: eso deja un "Ajuste manual" sin motivo.
export function DialogoInsumo({ insumo, unidades, onCerrar, onGuardado }: { insumo?: any; unidades: string[]; onCerrar: () => void; onGuardado: (id: string, msg: string) => void }) {
  const [f, setF] = useState({
    nombre: insumo?.nombre || '', unidad: insumo?.unidad || unidades[0] || 'pieza',
    stock_actual: '', stock_minimo: insumo ? String(num(insumo.stock_minimo)) : '',
    costo_unitario: insumo ? String(num(insumo.costo_unitario)) : '', controla_stock: insumo ? insumo.controla_stock !== false : true,
  });
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const set = (k: keyof typeof f, v: any) => setF(x => ({ ...x, [k]: v }));

  const guardar = async () => {
    if (!f.nombre.trim()) { setError('Escribe el nombre del insumo.'); return; }
    setOcupado(true);
    const datos: any = { nombre: f.nombre.trim(), unidad: f.unidad, stock_minimo: num(f.stock_minimo), costo_unitario: num(f.costo_unitario), controla_stock: f.controla_stock };
    try {
      if (insumo) { await inventarioService.actualizarInsumo(insumo.id, datos); onGuardado(insumo.id, 'Insumo guardado.'); }
      else { const { data } = await inventarioService.crearInsumo({ ...datos, stock_actual: num(f.stock_actual) }); onGuardado(data.id, `${datos.nombre} agregado.`); }
    } catch (e: any) { setError(e.response?.data?.error || 'No se pudo guardar.'); setOcupado(false); }
  };

  return (
    <Dialogo titulo={insumo ? 'Editar insumo' : 'Nuevo insumo'} onCerrar={onCerrar} ancho={460}>
      <Campo label="Nombre"><input autoFocus value={f.nombre} onChange={e => set('nombre', e.target.value)} placeholder="Ej. Pechuga de pollo" maxLength={150} style={entrada} /></Campo>
      <div style={dosColumnas}>
        <Campo label="Unidad">
          <select value={f.unidad} onChange={e => set('unidad', e.target.value)} style={entrada}>
            {[...new Set([...unidades, f.unidad])].map(u => <option key={u}>{u}</option>)}
          </select>
        </Campo>
        <Campo label="Costo por unidad ($)"><input type="number" min="0" step="any" inputMode="decimal" value={f.costo_unitario} onChange={e => set('costo_unitario', e.target.value)} style={entrada} /></Campo>
      </div>
      {f.controla_stock && (
        <div style={dosColumnas}>
          {!insumo && <Campo label="Stock inicial"><input type="number" min="0" step="any" inputMode="decimal" value={f.stock_actual} onChange={e => set('stock_actual', e.target.value)} style={entrada} /></Campo>}
          <Campo label="Stock mínimo"><input type="number" min="0" step="any" inputMode="decimal" value={f.stock_minimo} onChange={e => set('stock_minimo', e.target.value)} style={entrada} /></Campo>
        </div>
      )}
      {/* Sal, especias, gas: cuestan, pero nadie los cuenta */}
      <label style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px', borderRadius: 12, border: '1px solid var(--line)', cursor: 'pointer' }}>
        <input type="checkbox" checked={!f.controla_stock} onChange={e => set('controla_stock', !e.target.checked)} style={{ width: 20, height: 20, marginTop: 2, accentColor: 'var(--brand-texto)' }} />
        <span><b>No se inventaría</b><span style={{ display: 'block', fontSize: 14, color: 'var(--muted)' }}>Cuenta para el costo de las recetas, pero no se cuenta ni se descuenta. Ej. sal, especias, gas.</span></span>
      </label>
      {error && <div role="alert" style={{ color: 'var(--danger)', fontWeight: 700 }}>{error}</div>}
      <Botones onCerrar={onCerrar} onOk={guardar} ok={insumo ? 'Guardar cambios' : 'Guardar insumo'} ocupado={ocupado} />
    </Dialogo>
  );
}
